import {
  sanitizeDiagnosticEntityId,
  sanitizeDiagnosticEntityType,
  sanitizeDiagnosticId,
  sanitizeDiagnosticMetadata,
  type DiagnosticCategory,
  type DiagnosticEntityType,
  type DiagnosticEventName,
  type DiagnosticMetadataInput,
  type DiagnosticOutcome,
  type DiagnosticSeverity,
  normalizeStoredDiagnosticEvents,
  type StoredDiagnosticEvent,
  type StoredDiagnosticNormalizationOptions
} from "./diagnosticSchema";
import { APP_VERSION, APP_VERSION_CODE } from "./utils";

declare const __BUILD_ID__: string;

export type { DiagnosticOutcome } from "./diagnosticSchema";

export type DiagnosticInput = {
  name: DiagnosticEventName;
  category: DiagnosticCategory;
  severity?: DiagnosticSeverity;
  outcome?: DiagnosticOutcome;
  actionId?: string;
  requestId?: string;
  entityType?: DiagnosticEntityType;
  entityId?: string | number;
  metadata?: DiagnosticMetadataInput;
  /** Repeated network/presence events with the same key are collapsed inside this window. */
  coalesceKey?: string;
  coalesceMs?: number;
};

type AuthContext = {
  businessSlug: string;
  userId: number;
  deviceId: string;
  deviceName: string;
  token: string;
  diagnosticSessionId?: string;
};

const DB_NAME = "easyorders-pwa-diagnostics";
const DB_VERSION = 1;
const STORE = "outbox";
const SEQUENCE_KEY = "easyorders.pwa.diagnostics.sequence";
const MAX_OUTBOX_EVENTS = 400;
const MAX_EVENT_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const BATCH_SIZE = 80;
const FLUSH_DELAY_MS = 1_500;
const RETRY_DELAY_MS = 30_000;
const MAX_RETRY_DELAY_MS = 60 * 60 * 1000;

function randomId(): string {
  return crypto.randomUUID();
}

function browserFamily(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "edge";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/CriOS\//.test(ua)) return "chrome";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

function osFamily(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Windows/.test(ua)) return "windows";
  if (/Macintosh|Mac OS X/.test(ua)) return "macos";
  if (/Linux/.test(ua)) return "linux";
  return "other";
}

function displayMode(): "standalone" | "browser" {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone
    ? "standalone"
    : "browser";
}

function retryAfterDelay(value: string | null): number {
  if (!value) return RETRY_DELAY_MS;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;
  const retryAt = Date.parse(value);
  return Number.isFinite(retryAt) ? Math.max(0, retryAt - Date.now()) : RETRY_DELAY_MS;
}

function crashLocation(filename: string | undefined, line = 0, column = 0): {
  script?: string;
  line: number;
  column: number;
  fingerprint: string;
} {
  const script = filename
    ? filename.split(/[\\/]/).pop()?.split(/[?#]/)[0]?.slice(0, 120)
    : undefined;
  const location = `${script ?? "unknown"}:${line}:${column}`;
  let fingerprint = 2166136261;
  for (let index = 0; index < location.length; index += 1) {
    fingerprint ^= location.charCodeAt(index);
    fingerprint = Math.imul(fingerprint, 16777619);
  }
  return {
    script,
    line,
    column,
    fingerprint: Math.abs(fingerprint).toString(16)
  };
}

function promiseCrashLocation(reason: unknown): ReturnType<typeof crashLocation> {
  const stack = typeof reason === "object" && reason !== null &&
    typeof (reason as { stack?: unknown }).stack === "string"
    ? (reason as { stack: string }).stack
    : "";
  /* Only the generated bundle basename and numeric coordinates survive this extraction. The raw
     stack (which can contain messages, paths and query strings) is never stored or transmitted. */
  const match = stack.match(/(?:^|[\\/])([A-Za-z0-9._-]+\.js)(?:[?#][^:\s)]*)?:(\d+):(\d+)/m);
  return crashLocation(
    match?.[1],
    Number.parseInt(match?.[2] ?? "0", 10) || 0,
    Number.parseInt(match?.[3] ?? "0", 10) || 0
  );
}

function openOutbox(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("indexeddb_unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "event_id" });
        store.createIndex("occurred_at", "occurred_at");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("indexeddb_open_failed"));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("indexeddb_request_failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("indexeddb_transaction_aborted"));
    transaction.onerror = () => reject(transaction.error ?? new Error("indexeddb_transaction_failed"));
  });
}

class DiagnosticsService {
  private diagnosticSessionId = randomId();
  private sequence = 0;
  private auth: AuthContext | null = null;
  private memoryOutbox: StoredDiagnosticEvent[] = [];
  private dbPromise: Promise<IDBDatabase> | null = null;
  private flushPromise: Promise<void> | null = null;
  private flushTimer: number | null = null;
  private retryTimer: number | null = null;
  private started = false;
  private recent = new Map<string, number>();
  private reportedSessionIds = new Set<string>();

  newActionId(): string {
    return randomId();
  }

  configureAuth(next: AuthContext | null): void {
    this.auth = next;
    if (!next) {
      if (this.flushTimer !== null) window.clearTimeout(this.flushTimer);
      if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
      this.flushTimer = null;
      this.retryTimer = null;
      this.diagnosticSessionId = randomId();
      this.sequence = 0;
      try {
        localStorage.removeItem(SEQUENCE_KEY);
      } catch {
        // Storage may be disabled; the in-memory sequence still resets.
      }
      void this.clearOutbox();
    }
    const nextSessionId = sanitizeDiagnosticId(next?.diagnosticSessionId);
    if (nextSessionId && nextSessionId !== this.diagnosticSessionId) {
      this.diagnosticSessionId = nextSessionId;
      this.sequence = this.loadSequence(nextSessionId);
    }
    if (next) {
      if (!this.reportedSessionIds.has(this.diagnosticSessionId)) {
        this.reportedSessionIds.add(this.diagnosticSessionId);
        this.record({
          name: "app.lifecycle",
          category: "SYSTEM",
          outcome: "succeeded",
          metadata: { operation: "session_attached", online: navigator.onLine }
        });
      }
      this.scheduleFlush(0);
    }
  }

  getSessionId(): string {
    return this.diagnosticSessionId;
  }

  /** Prevents a failed login for one business being uploaded under a later login for another. */
  async prepareLogin(): Promise<void> {
    this.diagnosticSessionId = randomId();
    this.sequence = 0;
    try {
      localStorage.removeItem(SEQUENCE_KEY);
    } catch {
      // Storage may be disabled; the in-memory sequence still resets.
    }
    await this.clearOutbox();
  }

  private async clearOutbox(): Promise<void> {
    this.memoryOutbox = [];
    try {
      const db = await this.database();
      const transaction = db.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).clear();
      await transactionDone(transaction);
    } catch {
      // The in-memory fallback was still cleared.
    }
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.record({
      name: "app.lifecycle",
      category: "SYSTEM",
      outcome: "succeeded",
      metadata: { online: navigator.onLine, operation: "started" }
    });
    window.addEventListener("error", (event) => {
      const location = crashLocation(event.filename, event.lineno || 0, event.colno || 0);
      this.record({
        name: "app.error",
        category: "UI",
        severity: "ERROR",
        outcome: "failed",
        metadata: {
          source: "window",
          operation: "runtime_error",
          ...location
        },
        coalesceKey: `runtime_error:${location.fingerprint}`,
        coalesceMs: 10_000
      });
    });
    window.addEventListener("unhandledrejection", (event) => {
      const location = promiseCrashLocation(event.reason);
      this.record({
        name: "app.error",
        category: "UI",
        severity: "ERROR",
        outcome: "failed",
        metadata: { source: "promise", operation: "unhandled_rejection", ...location },
        coalesceKey: `unhandled_rejection:${location.fingerprint}`,
        coalesceMs: 10_000
      });
    });
    window.addEventListener("offline", () => {
      this.record({
        name: "connection.state",
        category: "NETWORK",
        severity: "WARNING",
        outcome: "offline",
        metadata: { online: false },
        coalesceKey: "connection_offline",
        coalesceMs: 5_000
      });
    });
    window.addEventListener("online", () => {
      this.record({
        name: "connection.state",
        category: "NETWORK",
        outcome: "online",
        metadata: { online: true },
        coalesceKey: "connection_online",
        coalesceMs: 5_000
      });
      this.scheduleFlush(0);
    });
  }

  beginAction(input: Omit<DiagnosticInput, "actionId" | "outcome">): string {
    const actionId = this.newActionId();
    this.record({ ...input, actionId, outcome: "started" });
    return actionId;
  }

  record(input: DiagnosticInput): void {
    try {
      const now = Date.now();
      if (input.coalesceKey) {
        const previous = this.recent.get(input.coalesceKey) ?? 0;
        if (now - previous < (input.coalesceMs ?? 5_000)) return;
        this.recent.set(input.coalesceKey, now);
        if (this.recent.size > 100) {
          for (const [key, time] of this.recent) {
            if (now - time > 60_000) this.recent.delete(key);
          }
        }
      }
      const sequence = this.nextSequence();
      const actionId = sanitizeDiagnosticId(input.actionId);
      const requestId = sanitizeDiagnosticId(input.requestId);
      const entityType = sanitizeDiagnosticEntityType(input.entityType);
      const entityId = entityType ? sanitizeDiagnosticEntityId(input.entityId) : undefined;
      const event: StoredDiagnosticEvent = {
        event_id: randomId(),
        occurred_at: now,
        diagnostic_session_id: this.diagnosticSessionId,
        sequence,
        severity: input.severity ?? "INFO",
        category: input.category,
        event_name: input.name,
        outcome: input.outcome,
        action_id: actionId,
        request_id: requestId,
        entity_type: entityId === undefined ? undefined : entityType,
        entity_id: entityId,
        platform: "pwa",
        app_version: APP_VERSION,
        app_version_code: APP_VERSION_CODE,
        build_id: __BUILD_ID__,
        context: {
          browser_family: browserFamily(),
          os_family: osFamily(),
          display_mode: displayMode(),
          online: navigator.onLine
        },
        metadata: sanitizeDiagnosticMetadata(input.metadata as Record<string, unknown> | undefined)
      };
      void this.enqueue(event).then(() => this.scheduleFlush(FLUSH_DELAY_MS)).catch(() => undefined);
    } catch {
      // Diagnostics are deliberately incapable of changing an application outcome.
    }
  }

  flush(): Promise<void> {
    if (!this.flushPromise) {
      this.flushPromise = this.flushInternal().finally(() => {
        this.flushPromise = null;
      });
    }
    return this.flushPromise;
  }

  private loadSequence(sessionId: string): number {
    try {
      const stored = JSON.parse(localStorage.getItem(SEQUENCE_KEY) ?? "null") as {
        sessionId?: unknown;
        value?: unknown;
      } | null;
      const value = Number(stored?.value);
      return stored?.sessionId === sessionId && Number.isFinite(value) && value >= 0
        ? value
        : 0;
    } catch {
      return 0;
    }
  }

  private nextSequence(): number {
    this.sequence += 1;
    /* start() runs before React restores a remembered Session. A pre-auth lifecycle event uses a
       disposable app-run session and must not overwrite the saved sequence for the authenticated
       diagnostic session that configureAuth() is about to restore. */
    if (!this.auth) return this.sequence;
    try {
      localStorage.setItem(SEQUENCE_KEY, JSON.stringify({
        sessionId: this.diagnosticSessionId,
        value: this.sequence
      }));
    } catch {
      // Sequence is still monotonic for this running page.
    }
    return this.sequence;
  }

  private database(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openOutbox().catch((error) => {
        this.dbPromise = null;
        throw error;
      });
    }
    return this.dbPromise;
  }

  private async enqueue(event: StoredDiagnosticEvent): Promise<void> {
    const cutoff = Date.now() - MAX_EVENT_AGE_MS;
    this.memoryOutbox = [...this.memoryOutbox, event]
      .filter((candidate) => candidate.occurred_at >= cutoff)
      .slice(-MAX_OUTBOX_EVENTS);
    try {
      const db = await this.database();
      await requestResult(db.transaction(STORE, "readwrite").objectStore(STORE).put(event));
      await this.trimDatabase(db);
    } catch {
      // The already-bounded in-memory copy remains available for this page lifetime.
    }
  }

  private async trimDatabase(db: IDBDatabase): Promise<void> {
    const cutoff = Date.now() - MAX_EVENT_AGE_MS;
    /* Read from the store, not the occurred_at index: malformed legacy rows may be unindexed and
       would otherwise evade both the age limit and poison-row cleanup indefinitely. */
    const all = await requestResult(
      db.transaction(STORE, "readonly").objectStore(STORE).getAll()
    ) as unknown[];
    const normalized = normalizeStoredDiagnosticEvents(all, this.normalizationOptions(cutoff));
    const sorted = normalized.events.sort((a, b) =>
      a.occurred_at - b.occurred_at || a.sequence - b.sequence
    );
    const removeCount = Math.max(0, sorted.length - MAX_OUTBOX_EVENTS);
    const remove = [
      ...normalized.discardedEventIds,
      ...sorted.slice(0, removeCount).map((event) => event.event_id)
    ];
    await this.removeDatabase(remove, db);
  }

  private async batch(sessionId = this.diagnosticSessionId): Promise<StoredDiagnosticEvent[]> {
    const cutoff = Date.now() - MAX_EVENT_AGE_MS;
    const options = this.normalizationOptions(cutoff, sessionId);
    const memory = normalizeStoredDiagnosticEvents(this.memoryOutbox, options);
    this.memoryOutbox = memory.events.slice(-MAX_OUTBOX_EVENTS);
    try {
      const db = await this.database();
      const stored = await requestResult(
        db.transaction(STORE, "readonly").objectStore(STORE).getAll()
      ) as unknown[];
      const normalized = normalizeStoredDiagnosticEvents(stored, options);
      await this.removeDatabase(normalized.discardedEventIds, db);
      const merged = new Map<string, StoredDiagnosticEvent>();
      for (const event of [...normalized.events, ...this.memoryOutbox]) {
        merged.set(event.event_id, event);
      }
      return [...merged.values()]
        .sort((a, b) => a.occurred_at - b.occurred_at || a.sequence - b.sequence)
        .slice(0, BATCH_SIZE);
    } catch {
      return this.memoryOutbox
        .filter((event) => event.diagnostic_session_id === sessionId)
        .slice(0, BATCH_SIZE);
    }
  }

  private normalizationOptions(
    cutoff: number,
    diagnosticSessionId = this.diagnosticSessionId
  ): StoredDiagnosticNormalizationOptions {
    return {
      diagnosticSessionId,
      cutoff,
      appVersion: APP_VERSION,
      appVersionCode: APP_VERSION_CODE,
      buildId: __BUILD_ID__,
      fallbackContext: {
        browser_family: browserFamily(),
        os_family: osFamily(),
        display_mode: displayMode(),
        online: navigator.onLine
      }
    };
  }

  private async removeDatabase(ids: string[], existingDb?: IDBDatabase): Promise<void> {
    if (!ids.length) return;
    try {
      const db = existingDb ?? await this.database();
      const transaction = db.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      for (const id of new Set(ids)) store.delete(id);
      await transactionDone(transaction);
    } catch {
      // The next batch will re-normalize and skip the same rows; valid later rows still proceed.
    }
  }

  private async remove(ids: string[]): Promise<void> {
    const sent = new Set(ids);
    this.memoryOutbox = this.memoryOutbox.filter((event) => !sent.has(event.event_id));
    try {
      const db = await this.database();
      const transaction = db.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      for (const id of ids) store.delete(id);
      await transactionDone(transaction);
    } catch {
      // A duplicate batch is harmless because event_id is idempotent on the server.
    }
  }

  private scheduleFlush(delay: number): void {
    if (!this.auth || !navigator.onLine) return;
    if (this.flushTimer !== null) window.clearTimeout(this.flushTimer);
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, delay);
  }

  private scheduleRetry(delay = RETRY_DELAY_MS): void {
    if (this.retryTimer !== null || !this.auth || !navigator.onLine) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, Math.max(1_000, Math.min(MAX_RETRY_DELAY_MS, delay)));
  }

  private async flushInternal(): Promise<void> {
    const auth = this.auth;
    const sessionId = this.diagnosticSessionId;
    if (!auth || !navigator.onLine) return;
    const events = await this.batch(sessionId);
    if (!events.length) return;
    if (this.auth !== auth || this.diagnosticSessionId !== sessionId) return;
    const requestId = randomId();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12_000);
    try {
      const response = await fetch("/api/diagnostics/events", {
        method: "POST",
        cache: "no-store",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-EasyOrders-Request-Id": requestId,
          "X-EasyOrders-Diagnostic-Session-Id": sessionId,
          "X-EasyOrders-Platform": "pwa",
          "X-EasyOrders-App-Version": `pwa-${APP_VERSION}`,
          "X-EasyOrders-App-Version-Code": String(APP_VERSION_CODE),
          "X-EasyOrders-Build-Id": __BUILD_ID__,
          "X-EasyOrders-Business": auth.businessSlug,
          "X-EasyOrders-User-Id": String(auth.userId),
          "X-EasyOrders-Device-Id": auth.deviceId,
          "X-EasyOrders-Device-Name": auth.deviceName,
          "X-EasyOrders-Session-Token": auth.token
        },
        body: JSON.stringify({ events }),
        signal: controller.signal
      });
      if (!response.ok) {
        this.scheduleRetry(response.status === 429
          ? retryAfterDelay(response.headers.get("Retry-After"))
          : RETRY_DELAY_MS);
        return;
      }
      await this.remove(events.map((event) => event.event_id));
      const more = await this.batch(sessionId);
      if (
        more.length &&
        this.auth === auth &&
        this.diagnosticSessionId === sessionId
      ) this.scheduleFlush(0);
    } catch {
      this.scheduleRetry();
    } finally {
      window.clearTimeout(timeout);
    }
  }
}

export const diagnostics = new DiagnosticsService();
